// OnChainCustomizationSection.js
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import LoadingComponent from "components/LoadingComponent/LoadingComponent";
import { StyledWhiteContainer } from "features/metadesk/settings/common.styles";
import {
  StyledCustomizationContainer,
  StyledLine,
  StyledGeneralSettingsContainer,
  StyledSettingTitle,
  StyledDescription,
} from "../OnChainDataSource.styles";
import { Button } from "@metacrm/metacrm-material-ui/dist/Button";
import Switch from "@metacrm/metacrm-material-ui/dist/Switch";
import { useIntl } from "react-intl";
import { useForm, FormProvider, Controller } from "react-hook-form";
import BlockchainNetworkPreference from "./BlockchainNetworkPreference";
import PortfolioDisplayPreference from "./PortfolioDisplayPreference";
import useGetPagePermission from "hooks/useGetPagePermission";
import FixedButton from "features/metadesk/settings/common/FixedButton";
import Prompt from "features/common/Prompt";
import { getChainList } from "features/helpers/utils";
import {
  useFetchOnchainDataSetting,
  useFetchEntityConfig,
} from "features/metadesk/redux/hooks";

const defaultValues = {
  enabled: false,
  blockchainNetworks: [],
  portfolio: {
    tokens: [],
    nfts: [],
  },
};

const OnChainCustomizationSection = ({ ecosystemName, entityName }) => {
  const { formatMessage } = useIntl();
  const pagePermission = useGetPagePermission();
  const { canEdit } = pagePermission("onchainData");

  const methods = useForm({ defaultValues, mode: "onSubmit" });
  const {
    control,
    watch,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;

  const isEnable = watch("enabled");
  const chainList = getChainList(ecosystemName.toLowerCase());

  const {
    onchainDataSetting,
    fetchOnchainDataSetting,
    updateOnchainDataSetting,
    fetchOnchainDataSettingPending,
  } = useFetchOnchainDataSetting();

  const { fetchEntityConfig } = useFetchEntityConfig();

  useEffect(() => {
    if (entityName && ecosystemName) {
      fetchOnchainDataSetting({
        entityName,
        ecosystem: ecosystemName.toLowerCase(),
      });
    }
  }, [entityName, ecosystemName]);

  // Set backend data as default values, in order to use isDirty to control save button
  useEffect(() => {
    if (onchainDataSetting && chainList.length > 0) {
      const backendChainIds = onchainDataSetting.chainIds || [];

      // show first five chains in the list
      const defaultChains = chainList.slice(0, 5);

      const additionalChains = chainList.filter(
        (chain) =>
          backendChainIds.includes(chain.value) &&
          !defaultChains.some((c) => c.value === chain.value)
      );

      const allDisplayedChains = [...defaultChains, ...additionalChains];

      const blockchainNetworks = allDisplayedChains.map((chain, index) => {
        if (backendChainIds.length > 0) {
          return {
            chainId: chain.value,
            checked: backendChainIds.includes(chain.value),
          };
        } else {
          return {
            chainId: chain.value,
            checked: index < 2, // if no backend setting, check the first two
          };
        }
      });

      const transformed = {
        enabled: onchainDataSetting.enabled,
        blockchainNetworks,
        portfolio: onchainDataSetting.portfolio,
      };

      reset(transformed);
    }
  }, [onchainDataSetting, chainList]);

  const onSubmit = async (data) => {
    const updatedData = {
      ecosystem: ecosystemName.toLowerCase(),
      enabled: data.enabled,
      chainIds: data.blockchainNetworks
        ?.filter((c) => c.checked)
        .map((c) => c.chainId),
      tokens: data.portfolio?.tokens || [],
      nfts: data.portfolio?.nfts || [],
    };

    await updateOnchainDataSetting({
      entityName,
      body: updatedData,
    });

    fetchOnchainDataSetting({
      entityName,
      ecosystem: ecosystemName.toLowerCase(),
    });
    fetchEntityConfig({
      entityName,
    });
  };

  return (
    <>
      <StyledWhiteContainer className="fade-up-more">
        {fetchOnchainDataSettingPending ? (
          <Box
            sx={{
              minHeight: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingComponent />
          </Box>
        ) : (
          <StyledCustomizationContainer ecosystem={ecosystemName}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* enabled Setting */}
                <StyledGeneralSettingsContainer>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "10px",
                    }}
                  >
                    <Box>
                      <StyledSettingTitle>
                        {formatMessage(
                          { id: "dataSourceSetting.enableEcosystem.title" },
                          { ecosystem: ecosystemName }
                        )}
                      </StyledSettingTitle>
                      <StyledDescription>
                        {formatMessage(
                          {
                            id: "dataSourceSetting.enableEcosystem.description",
                          },
                          { ecosystem: ecosystemName }
                        )}
                      </StyledDescription>
                    </Box>
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          iosSwitch
                          {...field}
                          checked={field.value}
                          disabled={!canEdit}
                        />
                      )}
                    />
                  </Box>
                </StyledGeneralSettingsContainer>

                {isEnable && ecosystemName !== "Aptos" && (
                  <>
                    <StyledLine showMargin={[false, true]} />

                    <BlockchainNetworkPreference
                      entityName={entityName}
                      ecosystemName={ecosystemName}
                      canEdit={canEdit}
                      chainList={chainList}
                    />

                    <StyledLine />

                    <PortfolioDisplayPreference
                      entityName={entityName}
                      ecosystemName={ecosystemName}
                      canEdit={canEdit}
                    />
                  </>
                )}
              </form>
            </FormProvider>
          </StyledCustomizationContainer>
        )}
      </StyledWhiteContainer>
      <FixedButton isShowGap={false}>
        <Button
          sx={{ width: "120px", marginLeft: "8px" }}
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || fetchOnchainDataSettingPending}
        >
          {formatMessage({ id: "global.btn.save" })}
        </Button>
      </FixedButton>
      <Prompt when={isDirty} />
    </>
  );
};

export default OnChainCustomizationSection;
